import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { LoadingScreen } from '../../../../../components/loading-screen';
import { MobileBlocker } from '../../../../../components/mobile-blocker';
import { BulkGroupStatus } from '../../../../../entity/api/bulk-group';
import { trackError } from '../../../../../error/tracking';
import { captureException } from '../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../sdk/request';
import { fetchBulkGroupsListImperative, useBulkGroupById } from '../../../../../services/storage';
import { tNamespace } from '../../../../../services/translation';
import { Feature, isFeatureAvailable } from '../../../../../shared/feature';

const t = tNamespace('app.funnel.bulk-import.$id.loading');

export const Route = createFileRoute('/app/funnel/bulk-import/$id/loading')({
  beforeLoad: function () {
    const queryClient = getQueryClient();
    // NOTE: we may want to query only the bulk group by id at this stage in the future
    return Promise.allSettled([fetchBulkGroupsListImperative(queryClient)]).catch(function (err) {
      captureException(
        err,
        {
          file: 'routes/app/funnel/bulk-import/$id/loading.tsx',
          method: 'beforeLoad',
          infoMessage: 'unable to fetch bulk import list from api',
        },
        trackError
      );

      throw err;
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { id: bulkGroupId }: { id: string } = Route.useParams();

  // TanStack Query hook
  const { data: bulkGroup } = useBulkGroupById(bulkGroupId);

  useEffect(
    function () {
      function navigateTo(path: string) {
        navigate({ to: path }).catch(function (err) {
          captureException(
            err,
            {
              file: 'routes/app/funnel/bulk-import/$id/loading.tsx',
              method: 'useEffect',
              infoMessage: `unable to navigate to ${path}`,
            },
            trackError
          );
        });
      }

      // Redirect to products page if no bulk group exists
      if (bulkGroup == null) {
        navigateTo('/app/products');
        return;
      }

      // Handle different bulk group statuses
      switch (bulkGroup.status) {
        case BulkGroupStatus.DRAFT:
          navigateTo(`/app/funnel/bulk-import/$id`);
          break;
        case BulkGroupStatus.COMPLETED:
          navigateTo(`/app/products/bulk-import`);
          break;
      }
    },
    [bulkGroup, navigate, bulkGroupId]
  );

  useEffect(function () {
    const timeout = setTimeout(function () {
      navigate({ to: `/app/products/bulk-import` }).catch(function (err) {
        captureException(
          err,
          {
            file: 'routes/app/funnel/bulk-import/$id/loading.tsx',
            method: 'useEffect',
            infoMessage: 'unable to navigate to products page',
          },
          trackError
        );
      });
    }, 2000);

    return function () {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/funnel/bulk-import/$id/loading.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate]
  );

  return (
    <>
      <MobileBlocker />
      <LoadingScreen text={t('title')} />
    </>
  );
}
